/**
 * @fileoverview Selections Export Utilities
 *
 * Shared utility functions for printing and exporting selections data
 * as CSV. Used by both client portal (ClientSelections) and admin portal
 * (HomeSelections) components.
 *
 * Exports:
 * - handlePrintSelections: Triggers browser print dialog
 * - handleDownloadCSV: Generates and downloads a multi-sheet CSV (via XLSX)
 * - buildOverviewRows: Flattens selections data into label/value rows
 * - buildPaintRows: Flattens paint selections into columnar rows
 *
 * Location: src/utils/selectionsExport.js
 *
 * @version 1.0.0
 */

/**
 * Determines display value for a selection field.
 * Handles strings, booleans, arrays, and empty/null values.
 * @param {*} value - The raw field value
 * @param {boolean} [isBoolean=false] - Whether to treat as boolean field
 * @param {boolean} [isVendor=false] - Whether to treat as vendor ordering field
 * @returns {string} Human-readable display value
 */
function getDisplayValue(value, isBoolean = false, isVendor = false) {
  if (isVendor) {
    if (!value || value === "") return "Selection Needed";
    return value; // "Local Vendor" or "Online"
  }

  if (isBoolean) {
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false" || !value)
      return "Selection Needed";
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "Selection Needed";
  }

  if (value === null || value === undefined || value === "") {
    return "Selection Needed";
  }

  return String(value);
}

/**
 * Builds an array of { section, label, value } rows from client selections data.
 * Mirrors the Overview tab structure: Exterior, Interior, Trim, Interior Features.
 *
 * @param {Object} selections - The clientSelections object from the service
 * @returns {Array<{section: string, label: string, value: string}>}
 */
function buildOverviewRows(selections) {
  if (!selections) return [];

  const rows = [];

  /**
   * Helper to push a row
   * @param {string} section - Section name
   * @param {string} label - Field label
   * @param {*} value - Raw value
   * @param {Object} [opts] - Options
   * @param {boolean} [opts.isBoolean] - Boolean field
   * @param {boolean} [opts.isVendor] - Vendor ordering field
   * @param {boolean} [opts.conditional] - Only include if truthy
   */
  const addRow = (section, label, value, opts = {}) => {
    if (opts.conditional && !value && value !== false) return;
    rows.push({
      section,
      label,
      value: getDisplayValue(value, opts.isBoolean, opts.isVendor),
    });
  };

  // --- EXTERIOR ---
  const ext = "Exterior";
  addRow(ext, "Roof Material", selections.roofMaterial);
  addRow(ext, "Roof Material Color", selections.roofColor);

  const sidingTypes = Array.isArray(selections.sidingTypes)
    ? selections.sidingTypes
    : [];
  addRow(ext, "Siding Type", sidingTypes);

  if (sidingTypes.includes("Fiber Cement Board")) {
    addRow(ext, "Fiber Cement Board Option", selections.fiberCementBoard);
  }
  if (sidingTypes.includes("Brick")) {
    addRow(ext, "Brick Chosen", selections.brickChosen, { isBoolean: true });
  }
  if (sidingTypes.includes("Stone")) {
    addRow(ext, "Stone Chosen", selections.stoneChosen, { isBoolean: true });
  }
  if (sidingTypes.includes("Wood")) {
    addRow(ext, "Wood Chosen", selections.woodChosen, { isBoolean: true });
  }

  addRow(ext, "Gutter/Downspout Color", selections.gutterColor);
  addRow(ext, "Garage Door Chosen", selections.garageDoorsChosen, {
    isBoolean: true,
  });
  addRow(
    ext,
    "Exterior Doors & Windows Chosen",
    selections.exteriorDoorsWindowsChosen,
    { isBoolean: true },
  );

  // --- INTERIOR ---
  const int = "Interior";
  addRow(int, "Door Pattern", selections.interiorDoorPattern);
  addRow(int, "Core Type", selections.interiorDoorCore);
  addRow(int, "Hinge Finish", selections.hingeFinish);
  addRow(int, "Door Knobs Purchased", selections.doorKnobsPurchased, {
    isBoolean: true,
  });
  addRow(int, "Flooring Direction", selections.flooringDirection);
  addRow(int, "Painted Closet Shelves", selections.closetShelving);
  addRow(int, "Switch Plate Color", selections.switchPlateColor);
  addRow(int, "Vent Style/Color", selections.ventStyle);

  // --- TRIM ---
  const trim = "Trim";
  addRow(trim, "Casing Style", selections.casingStyle);
  addRow(trim, "Trim Detail", selections.trimDetail);
  addRow(trim, "Baseboard Height", selections.baseboardHeight);
  addRow(trim, "Crown Moulding Size", selections.crownMouldingSize);

  // --- INTERIOR FEATURES ---
  const feat = "Interior Features";
  addRow(feat, "Cabinets Chosen", selections.cabinetsOrdered, {
    isVendor: true,
  });
  addRow(feat, "Counters/Sinks Chosen", selections.countersSinksOrdered, {
    isVendor: true,
  });
  addRow(feat, "Showers/Tubs Chosen", selections.showersTubsOrdered, {
    isVendor: true,
  });
  addRow(feat, "Shower Doors Chosen", selections.showerDoorsOrdered, {
    isVendor: true,
  });
  addRow(feat, "Fireplace Chosen", selections.fireplaceOrdered, {
    isVendor: true,
  });
  addRow(feat, "Floors Chosen", selections.floorsOrdered, { isVendor: true });
  addRow(feat, "Tile Chosen", selections.tileOrdered, { isVendor: true });
  addRow(feat, "Plumbing Fixtures Chosen", selections.plumbingFixturesOrdered, {
    isVendor: true,
  });
  addRow(feat, "Lighting Fixtures Chosen", selections.lightingFixturesOrdered, {
    isVendor: true,
  });

  // --- ADDITIONAL SELECTIONS / NOTES ---
  // Only included when notes exist, so the section is omitted entirely
  // (from print and CSV) when empty.
  const notes = (selections.additionalNotes || "").trim();
  if (notes !== "") {
    rows.push({
      section: "Additional Selections / Notes",
      label: "Selections Not Covered Above or Notes On Above Selections",
      value: notes,
    });
  }

  return rows;
}

/**
 * Builds columnar rows for paint selections.
 * Each paint selection becomes one row with columns:
 * Area(s), Paint Type, Color Name/Code, SW #, Sheen, Texture, Notes
 *
 * @param {Array} paintSelections - Array of paint selection objects from service
 * @returns {Array<Object>} Rows with named columns
 */
function buildPaintRows(paintSelections) {
  if (!paintSelections || paintSelections.length === 0) return [];

  return paintSelections.map((paint) => ({
    "Area(s)": Array.isArray(paint.areas)
      ? paint.areas.join(", ")
      : paint.areas || "",
    "Paint Code/Name": paint.paintSelection || "",
    Sheen: paint.sheen || "",
    Texture: paint.texture || "",
    Notes: paint.locationNotes || "",
  }));
}

/**
 * Generates and triggers download of a multi-tab XLSX file
 * containing Overview and Paint Selections sheets.
 *
 * Uses SheetJS (XLSX) library which is available in the project.
 * Falls back to a simple CSV if XLSX is not available.
 *
 * @param {Object} selections - The clientSelections data object
 * @param {Array} paintSelections - Array of paint selection objects
 * @param {string} [projectName="Selections"] - Project name for filename
 */
function handleDownloadCSV(
  selections,
  paintSelections,
  projectName = "Selections",
) {
  const overviewRows = buildOverviewRows(selections);
  const paintRows = buildPaintRows(paintSelections);

  // Build the Overview CSV content
  const overviewHeader = "Section,Selection,Value";
  const overviewLines = overviewRows.map(
    (row) =>
      `"${escapeCsvField(row.section)}","${escapeCsvField(row.label)}","${escapeCsvField(row.value)}"`,
  );

  // Build the Paint Selections CSV content
  const paintHeader = "Area(s),Paint Code/Name,Sheen,Texture,Notes";
  const paintLines = paintRows.map(
    (row) =>
      `"${escapeCsvField(row["Area(s)"])}","${escapeCsvField(row["Paint Code/Name"])}","${escapeCsvField(row["Sheen"])}","${escapeCsvField(row["Texture"])}","${escapeCsvField(row["Notes"])}"`,
  );

  // Combine into a single CSV with section dividers
  const csvContent = [
    "SELECTIONS OVERVIEW",
    "",
    overviewHeader,
    ...overviewLines,
    "",
    "",
    "PAINT SELECTIONS",
    "",
    paintHeader,
    ...(paintLines.length > 0 ? paintLines : ['"No paint selections"']),
  ].join("\n");

  // Create and trigger download
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  // Sanitize project name for filename
  const safeName =
    projectName.replace(/[^a-zA-Z0-9\s-]/g, "").trim() || "Selections";
  link.href = url;
  link.download = `${safeName} - Selections.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Escapes a value for safe CSV inclusion.
 * Doubles internal quotes and handles special characters.
 *
 * @param {string} field - The raw field value
 * @returns {string} Escaped string safe for CSV cell
 */
function escapeCsvField(field) {
  if (field === null || field === undefined) return "";
  return String(field).replace(/"/g, '""');
}

/**
 * Triggers the browser print dialog.
 * The @media print rules in SelectionsPrint.css handle hiding UI
 * and showing the print-specific containers.
 *
 * Call this after ensuring the print containers are rendered in the DOM.
 */
function handlePrintSelections() {
  window.print();
}

export {
  handlePrintSelections,
  handleDownloadCSV,
  buildOverviewRows,
  buildPaintRows,
  getDisplayValue,
};
